import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { Skeleton } from "@/src/common/components/ui/skeleton";
import { Switch } from "@/src/common/components/ui/switch";
import { toastService } from "@/src/common/services/toast.service";
import { defaultPreferences, type NotificationPreferences, notificationsService } from "@/src/notifications/services/notifications.service";

type ToggleKey = "feed" | "jobs" | "studentCentral" | "messaging";

const TOGGLES: { key: ToggleKey; labelKey: string; helpKey: string }[] = [
	{ key: "feed", labelKey: "toggle.feed_label", helpKey: "toggle.feed_help" },
	{ key: "jobs", labelKey: "toggle.jobs_label", helpKey: "toggle.jobs_help" },
	{ key: "studentCentral", labelKey: "toggle.student_central_label", helpKey: "toggle.student_central_help" },
	{ key: "messaging", labelKey: "toggle.messaging_label", helpKey: "toggle.messaging_help" },
];

export function NotificationsSection() {
	const { t } = useTranslation("notifications");
	const { profile } = useAuth();
	const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [saving, setSaving] = useState<ToggleKey | null>(null);

	useEffect(() => {
		if (!profile?.id) {
			setIsLoading(false);
			return;
		}
		const userId = profile.id;
		let cancelled = false;
		(async () => {
			try {
				const loaded = await notificationsService.get(userId);
				if (!cancelled) setPrefs(loaded);
			} catch (err) {
				if (!cancelled) {
					toastService.error(err instanceof Error ? err.message : t("toast.error_load"));
					setPrefs({ userId, ...defaultPreferences });
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [profile?.id, t]);

	const onToggle = async (key: ToggleKey, value: boolean) => {
		if (!prefs) return;
		const previous = prefs;
		const next: NotificationPreferences = { ...prefs, [key]: value };
		setPrefs(next);
		setSaving(key);
		try {
			const saved = await notificationsService.upsert(next);
			setPrefs(saved);
			toastService.success(t("toast.saved"));
		} catch (err) {
			setPrefs(previous);
			toastService.error(err instanceof Error ? err.message : t("toast.error_save"));
		} finally {
			setSaving(null);
		}
	};

	if (!profile?.id) return <p className="text-sm text-muted-foreground">{t("signed_out")}</p>;
	if (isLoading || !prefs) {
		return (
			<ul className="flex flex-col gap-2.5" aria-busy="true" aria-live="polite">
				{TOGGLES.map(({ key }) => (
					<li key={key} className="flex items-center justify-between gap-3 rounded-s border border-border bg-background px-3 py-2.5">
						<div className="flex flex-1 flex-col gap-1.5">
							<Skeleton className="h-3.5 w-32 rounded" />
							<Skeleton className="h-3 w-48 rounded" />
						</div>
						<Skeleton className="h-6 w-11 rounded-full" />
					</li>
				))}
			</ul>
		);
	}

	return (
		<ul className="flex flex-col gap-2.5">
			{TOGGLES.map(({ key, labelKey, helpKey }) => (
				<li key={key} className="flex items-center justify-between gap-3 rounded-s border border-border bg-background px-3 py-2.5">
					<div className="flex flex-col">
						<p className="text-sm font-medium text-foreground">{t(labelKey as "toggle.jobs_label")}</p>
						<p className="text-xs text-muted-foreground">{t(helpKey as "toggle.jobs_help")}</p>
					</div>
					<Switch
						isSelected={prefs[key]}
						isDisabled={saving === key}
						onChange={(checked) => void onToggle(key, checked)}
						aria-label={t(labelKey as "toggle.jobs_label")}
					>
						<Switch.Content>
							<Switch.Control>
								<Switch.Thumb />
							</Switch.Control>
						</Switch.Content>
					</Switch>
				</li>
			))}
		</ul>
	);
}
